import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'

import { useWindowSize } from 'src/common/hooks'

interface PortalTargetProps {
  className?: string
  id: string
  media?: 'desktop' | 'mobile'
}

const PortalTarget = (props: PortalTargetProps) => {
  const { className, id, media = 'desktop' } = props

  return <div className={className} id={`${media}_${id}`} />
}

interface PortalWrapperProps {
  id: string
  width?: number
  isAdaptive?: boolean
}

const PortalWrapper = (props: React.PropsWithChildren<PortalWrapperProps>) => {
  const { id, width = 768, isAdaptive = true, children } = props

  const { pathname } = useLocation()

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)

  const windowSize = useWindowSize()

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const resolveTarget = () => {
      const currentWidth =
        (typeof window !== 'undefined' && window.innerWidth) ||
        windowSize.width ||
        0
      const targetId = isAdaptive
        ? `${currentWidth >= width ? 'desktop' : 'mobile'}_${id}`
        : `desktop_${id}`
      return document.getElementById(targetId)
    }

    const target = resolveTarget()
    if (target) {
      setPortalTarget(target)
      return
    }

    const observer = new MutationObserver(() => {
      const el = resolveTarget()
      if (el) {
        setPortalTarget(el)
        observer.disconnect()
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })

    let raf: number | null = null
    if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
      raf = window.requestAnimationFrame(() => {
        const el = resolveTarget()
        if (el) {
          setPortalTarget(el)
          observer.disconnect()
        }
      })
    }

    return () => {
      observer.disconnect()
      if (raf) {
        window.cancelAnimationFrame(raf)
      }
    }
  }, [id, isAdaptive, width, windowSize.width, pathname])

  if (!portalTarget) {
    return null
  }

  return createPortal(children, portalTarget)
}

export default {
  Target: PortalTarget,
  Wrapper: PortalWrapper,
}
